import { useState, useMemo, useCallback, useEffect } from 'react';
import type { VariableNode } from 'services/entities/VariablesEntity';

// Resolves `path` only within contexts that are always structurally correct: a direct child of
// wherever the user currently is (descending), or a node already on the ancestor chain (ascending
// back to a breadcrumb). CRM data can produce two unrelated nodes that share the same `path`
// string in different branches of the tree (the wire path is stamped by CRM object type, not the
// real relationship it was reached through) — searching the whole tree for "the" node with a
// given path can resolve to the wrong one. Since every real caller already has the node it's
// navigating to/from in hand, we never need to search outside these two known-safe contexts.
// `chain` is the real root-to-current ancestor path (root inclusive); returns a new root-exclusive
// navigation stack, or null if `path` isn't a child of current or an ancestor already in `chain`.
function resolveTarget(chain: VariableNode[], path: string): VariableNode[] | null {
  const current = chain[chain.length - 1];

  const child = current.variables?.find((v) => v.path === path);
  if (child) return [...chain.slice(1), child];

  const ancestorIndex = chain.findIndex((node) => node.path === path);
  if (ancestorIndex !== -1) return chain.slice(1, ancestorIndex + 1);

  return null;
}

export function useVariableNavigation(data: VariableNode | null) {
  // Root-exclusive stack of the real nodes navigated through; activeNode is the last entry, or
  // `data` itself when empty. Real node references, not re-derived path strings.
  const [navigationStack, setNavigationStack] = useState<VariableNode[]>([]);
  const [initialLoad, setInitialLoad] = useState(true);

  // A fresh `data` tree (e.g. after a CRM link/unlink refetch) invalidates any node references
  // held from the previous tree — they're not valid positions in the new one.
  useEffect(() => {
    setNavigationStack([]);
  }, [data]);

  const activeNode = useMemo(() => {
    if (!data) return null;
    return navigationStack.length > 0 ? navigationStack[navigationStack.length - 1] : data;
  }, [data, navigationStack]);

  const breadcrumbs = useMemo(() => {
    if (initialLoad || !data || navigationStack.length === 0) return [];
    return [data, ...navigationStack];
  }, [initialLoad, data, navigationStack]);

  const selectPath = useCallback(
    (path: string) => {
      setInitialLoad(false);
      if (!data) return;
      setNavigationStack((stack) => resolveTarget([data, ...stack], path) ?? stack);
    },
    [data]
  );

  const resetPath = useCallback(() => setNavigationStack([]), []);

  return { activeNode, breadcrumbs, currentPath: activeNode?.path ?? '', selectPath, resetPath, initialLoad };
}
